import { useMemo } from 'react';
import { baseGridDefaults, defaultPageSize } from './gridDefaults';
import type { GridColumnWidthModel, PersistedState } from './gridState';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid';

type UrlState = {
  page?: number;
  pageSize?: number;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
} | null;

type UseGridInitialStateParams<T extends GridValidRowModel> = {
  columns: Array<GridColDef<T>>;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  urlState: UrlState;
  persistedState: PersistedState | null;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
};

type UseGridInitialStateResult = {
  resolvedPageSizeOptions: number[];
  resolvedInitialPageSize: number;
  initialPaginationModel: GridPaginationModel;
  initialSortModel: GridSortModel;
  initialFilterModel: GridFilterModel;
  initialColumnVisibilityModel: GridColumnVisibilityModel;
  initialColumnWidthModel: GridColumnWidthModel;
};

export const useGridInitialState = <T extends GridValidRowModel>({
  columns,
  pageSizeOptions,
  initialPageSize,
  urlState,
  persistedState,
  initialSortModel: initialSortModelProp,
  initialFilterModel: initialFilterModelProp,
  columnVisibilityModel,
  columnVisibilityPresets,
  columnVisibilityPreset,
}: UseGridInitialStateParams<T>): UseGridInitialStateResult => {
  const resolvedPageSizeOptions = useMemo(() => {
    const fallbackOptions = baseGridDefaults.pageSizeOptions;
    if (!pageSizeOptions || pageSizeOptions.length === 0) {
      return fallbackOptions;
    }
    const uniqueValidOptions = Array.from(
      new Set(pageSizeOptions.filter((value) => Number.isFinite(value) && value > 0)),
    );
    const withoutFive = uniqueValidOptions.filter((value) => value !== 5);
    if (withoutFive.length === 0) {
      return fallbackOptions;
    }
    if (withoutFive.length > 1) {
      return withoutFive;
    }
    const onlyOption = withoutFive[0];
    return [onlyOption, ...fallbackOptions.filter((value) => value !== onlyOption)];
  }, [pageSizeOptions]);
  const resolvedInitialPageSize = useMemo(() => {
    if (initialPageSize && resolvedPageSizeOptions.includes(initialPageSize)) {
      return initialPageSize;
    }
    if (resolvedPageSizeOptions.includes(defaultPageSize)) {
      return defaultPageSize;
    }
    return resolvedPageSizeOptions[0] ?? defaultPageSize;
  }, [initialPageSize, resolvedPageSizeOptions]);

  const isValidPageSize = (value: number | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0;
  const normalisePageSize = (value: number | undefined): number => {
    if (isValidPageSize(value) && resolvedPageSizeOptions.includes(value)) {
      return value;
    }
    return resolvedInitialPageSize;
  };
  const normaliseSortModel = (model: GridSortModel): GridSortModel => {
    if (model.length <= 1) return model;
    return [model[0]];
  };

  const initialPage = Number.isFinite(persistedState?.page) ? Number(persistedState?.page) : 0;
  const persistedPageSize = normalisePageSize(persistedState?.pageSize);
  const initialSortModel = normaliseSortModel(
    urlState?.sortModel && Array.isArray(urlState.sortModel)
      ? urlState.sortModel
      : persistedState && Array.isArray(persistedState.sortModel)
        ? (persistedState.sortModel as GridSortModel)
        : (initialSortModelProp ?? baseGridDefaults.initialSortModel ?? []),
  );
  const initialFilterModel: GridFilterModel =
    urlState?.filterModel && Array.isArray(urlState.filterModel.items)
      ? urlState.filterModel
      : persistedState?.filterModel && Array.isArray(persistedState.filterModel.items)
        ? (persistedState.filterModel as GridFilterModel)
        : (initialFilterModelProp ?? baseGridDefaults.initialFilterModel ?? { items: [] });

  const autoCompactPreset = useMemo(() => {
    const model: GridColumnVisibilityModel = {};
    columns.forEach((column) => {
      if (column.field === 'description' || column.field === 'projectName') {
        model[column.field] = false;
      }
    });
    return Object.keys(model).length > 0 ? model : null;
  }, [columns]);

  const resolvedPresets =
    columnVisibilityPresets ??
    (autoCompactPreset ? { default: {}, compact: autoCompactPreset } : undefined);
  const resolvedPresetKey =
    columnVisibilityPreset ??
    (resolvedPresets ? Object.keys(resolvedPresets)[0] : undefined) ??
    null;
  const resolvedPresetModel =
    resolvedPresetKey && resolvedPresets ? resolvedPresets[resolvedPresetKey] : undefined;
  const initialColumnVisibilityModel =
    columnVisibilityModel ?? persistedState?.columnVisibilityModel ?? resolvedPresetModel ?? {};
  const initialColumnWidthModel = persistedState?.columnWidthModel ?? {};

  const initialPageState = Number.isFinite(urlState?.page) ? Number(urlState?.page) : initialPage;
  const initialPageSizeState = normalisePageSize(urlState?.pageSize ?? persistedPageSize);

  return {
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    initialPaginationModel: { page: initialPageState, pageSize: initialPageSizeState },
    initialSortModel,
    initialFilterModel,
    initialColumnVisibilityModel,
    initialColumnWidthModel,
  };
};
